import { createSlice } from "@reduxjs/toolkit";
import {
  getOrders,
  getOrdersbyFR,
  getClientOrders,
  getQuotesStats,
  getOrdersStats,
  addNewOrder,
  updateOrder,
  updateOrdersBulk,
  deleteOrder,
  getOrdersReceivers,
  getOrdersReceiversByName,
  addNewOrderReceiver,
  updateOrderReceiver,
  deleteOrderReceiver,
  getOrdersSenders,
  getOrdersSendersByName,
  addNewOrderSender,
  updateOrderSender,
  deleteOrderSender,
  getCustomClearances,
  addNewCustomClearance,
  updateCustomClearance,
  deleteCustomClearance,
  getCustomDeclarations,
  addNewCustomDeclaration,
  updateCustomDeclaration,
  deleteCustomDeclaration,
} from "./thunk";

export const initialState = {
  orders: [],
  updatedOrders: [],
  error: {},
  isLoading: false,
  isOrdersSuccess: false,
  isOrdersFailed: false,

  clientOrders: [],
  errorClient: {},
  isClientLoading: false,
  isClientOrdersSuccess: false,
  isClientOrdersFailed: false,

  quotesStats: [],
  ordersStats: [],
  isQuotesStatsSuccess: false,
  isOrdersStatsSuccess: false,

  ordersReceivers: [],
  ordersReceiversByName: [],
  errorReceivers: {},
  isLoadingReceivers: false,
  isOrdersReceiversSuccess: false,
  isOrdersReceiversFailed: false,

  ordersSenders: [],
  ordersSendersByName: [],
  errorSenders: {},
  isLoadingSenders: false,
  isOrdersSendersSuccess: false,
  isOrdersSendersFailed: false,

  customClearances: [],
  errorCustomClearances: {},
  isLoadingCustomClearances: false,
  isCustomClearancesSuccess: false,
  isCustomClearancesFailed: false,

  customDeclarations: [],
  errorCustomDeclarations: {},
  isLoadingCustomDeclarations: false,
  isCustomDeclarationsSuccess: false,
  isCustomDeclarationsFailed: false,
};

const ordersSlice = createSlice({
  name: "order",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    //==================================[ /getOrders/ ]=======================================
    builder.addCase(getOrders.pending, (state) => {
      state.isLoading = true;
      state.isOrdersSuccess = false;
      state.error = null;
    });
    builder.addCase(getOrders.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isOrdersSuccess = true;
      state.orders = action.payload;
    });
    builder.addCase(getOrders.rejected, (state, action) => {
      state.isLoading = false;
      state.isOrdersSuccess = false;
      state.error = action.error.message;
    });

    //==================================[ /getClientOrders/ ]=======================================
    builder.addCase(getClientOrders.pending, (state) => {
      state.isClientLoading = true;
      state.isClientOrdersSuccess = false;
      state.errorClient = null;
    });
    builder.addCase(getClientOrders.fulfilled, (state, action) => {
      state.isClientLoading = false;
      state.isClientOrdersSuccess = true;
      state.clientOrders = action.payload;
    });
    builder.addCase(getClientOrders.rejected, (state, action) => {
      state.isClientLoading = false;
      state.isClientOrdersSuccess = false;
      state.errorClient = action.error.message;
    });

    //==================================[ /getOrdersbyFR/ ]=======================================
    builder.addCase(getOrdersbyFR.pending, (state) => {
      state.isLoading = true;
      state.isOrdersSuccess = false;
      state.error = null;
    });
    builder.addCase(getOrdersbyFR.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isOrdersSuccess = true;
      state.orders = action.payload;
    });
    builder.addCase(getOrdersbyFR.rejected, (state, action) => {
      state.isLoading = false;
      state.isOrdersSuccess = false;
      state.error = action.error.message;
    });

    builder.addCase(getQuotesStats.fulfilled, (state, action) => {
      state.quotesStats = action.payload;
      state.isQuotesStatsFail = false;
      state.isQuotesStatsSuccess = true;
    });

    builder.addCase(getQuotesStats.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || null;
      state.isQuotesStatsFail = true;
      state.isQuotesStatsSuccess = false;
    });

    builder.addCase(getOrdersStats.fulfilled, (state, action) => {
      state.ordersStats = action.payload;
      state.isOrdersStatsFail = false;
      state.isOrdersStatsSuccess = true;
    });

    builder.addCase(getOrdersStats.rejected, (state, action) => {
      state.error = action.payload?.error || action.error?.message || null;
      state.isOrdersStatsFail = true;
      state.isOrdersStatsSuccess = false;
    });

    builder.addCase(addNewOrder.fulfilled, (state, action) => {
      state.orders.push(action.payload);
      state.isOrderAdd = true;
      state.isOrderAddFail = false;
    });

    builder.addCase(addNewOrder.rejected, (state, action) => {
      state.error = action.payload.error || null;
      state.isOrderAdd = false;
      state.isOrderAddFail = true;
    });

    builder.addCase(updateOrder.fulfilled, (state, action) => {
      state.orders = state.orders.map((order) =>
        order._id.toString() === action.payload._id.toString()
          ? { ...order, ...action.payload }
          : order
      );
      state.isOrderUpdate = true;
      state.isOrderUpdateFail = false;
    });

    builder.addCase(updateOrder.rejected, (state, action) => {
      state.error = action.payload.error || null;
      state.isOrderUpdate = false;
      state.isOrderUpdateFail = true;
    });

    builder.addCase(updateOrdersBulk.fulfilled, (state, action) => {
      const updatedOrders = action.payload;

      updatedOrders.forEach((updated) => {
        const index = state.orders.findIndex((o) => o._id === updated._id);
        if (index !== -1) {
          state.orders[index] = { ...state.orders[index], ...updated };
        } else {
          state.orders.push(updated);
        }
      });

      state.isOrderUpdate = true;
      state.isOrderUpdateFail = false;
    });

    builder.addCase(updateOrdersBulk.rejected, (state, action) => {
      console.error("Bulk update rejected:", action.payload);
      state.error = action.payload?.error || action.payload || null;
      state.isOrderUpdate = false;
      state.isOrderUpdateFail = true;
    });

    builder.addCase(deleteOrder.fulfilled, (state, action) => {
      state.orders = (state.orders || []).filter(
        (order) => order._id.toString() !== action.payload.id.toString()
      );

      state.isOrderDelete = true;
      state.isOrderDeleteFail = false;
    });

    builder.addCase(deleteOrder.rejected, (state, action) => {
      state.error = action.payload.error || null;
      state.isOrderDelete = false;
      state.isOrderDeleteFail = true;
    });

    //==================================[ /getOrdersReceivers/ ]=======================================
    builder.addCase(getOrdersReceivers.pending, (state) => {
      state.isLoadingReceivers = true;
      state.isOrdersReceiversSuccess = false;
      state.errorReceivers = null;
    });

    builder.addCase(getOrdersReceivers.fulfilled, (state, action) => {
      state.isLoadingReceivers = false;
      state.isOrdersReceiversSuccess = true;
      state.ordersReceivers = action.payload;
    });

    builder.addCase(getOrdersReceivers.rejected, (state, action) => {
      state.isLoadingReceivers = false;
      state.isOrdersReceiversSuccess = false;
      state.errorReceivers = action.error.message;
    });

    builder.addCase(getOrdersReceiversByName.fulfilled, (state, action) => {
      state.ordersReceiversByName = action.payload;
      state.isOrdersReceiversByNameFail = false;
      state.isOrdersReceiversByNameSuccess = true;
    });

    builder.addCase(getOrdersReceiversByName.rejected, (state, action) => {
      state.error = action.payload.error || null;
      state.isOrdersReceiversByNameFail = true;
      state.isOrdersReceiversByNameSuccess = false;
    });

    builder.addCase(addNewOrderReceiver.fulfilled, (state, action) => {
      state.ordersReceivers.push(action.payload);
      state.isOrderReceiverAdd = true;
      state.isOrderReceiverAddFail = false;
    });

    builder.addCase(addNewOrderReceiver.rejected, (state, action) => {
      state.errorReceivers = action.payload.error || null;
      state.isOrderReceiverAdd = false;
      state.isOrderReceiverAddFail = true;
    });

    builder.addCase(updateOrderReceiver.fulfilled, (state, action) => {
      state.ordersReceivers = state.ordersReceivers.map((orderReceiver) =>
        orderReceiver._id.toString() === action.payload._id.toString()
          ? { ...orderReceiver, ...action.payload }
          : orderReceiver
      );
      state.isOrderReceiverUpdate = true;
      state.isOrderReceiverUpdateFail = false;
    });

    builder.addCase(updateOrderReceiver.rejected, (state, action) => {
      state.errorReceiver = action.payload.error || null;
      state.isOrderReceiverUpdate = false;
      state.isOrderReceiverUpdateFail = true;
    });

    builder.addCase(deleteOrderReceiver.fulfilled, (state, action) => {
      state.ordersReceivers = (state.ordersReceivers || []).filter(
        (orderReceiver) =>
          orderReceiver._id.toString() !== action.payload.id.toString()
      );

      state.isOrderReceiverDelete = true;
      state.isOrderReceiverDeleteFail = false;
    });

    builder.addCase(deleteOrderReceiver.rejected, (state, action) => {
      state.errorReceiver = action.payload.error || null;
      state.isOrderReceiverDelete = false;
      state.isOrderReceiverDeleteFail = true;
    });

    //==================================[ /getOrdersSenders/ ]=======================================
    builder.addCase(getOrdersSenders.pending, (state) => {
      state.isLoadingSenders = true;
      state.isOrdersSendersSuccess = false;
      state.errorSenders = null;
    });

    builder.addCase(getOrdersSenders.fulfilled, (state, action) => {
      state.isLoadingSenders = false;
      state.isOrdersSendersSuccess = true;
      state.ordersSenders = action.payload;
    });

    builder.addCase(getOrdersSenders.rejected, (state, action) => {
      state.isLoadingSenders = false;
      state.isOrdersSendersSuccess = false;
      state.errorSenders = action.error.message;
    });

    builder.addCase(getOrdersSendersByName.fulfilled, (state, action) => {
      state.ordersSendersByName = action.payload;
      state.isOrdersSendersByNameFail = false;
      state.isOrdersSendersByNameSuccess = true;
    });

    builder.addCase(getOrdersSendersByName.rejected, (state, action) => {
      state.error = action.payload.error || null;
      state.isOrdersSendersByNameFail = true;
      state.isOrdersSendersByNameSuccess = false;
    });

    builder.addCase(addNewOrderSender.fulfilled, (state, action) => {
      state.ordersSenders.push(action.payload);
      state.isOrderSenderAdd = true;
      state.isOrderSenderAddFail = false;
    });

    builder.addCase(addNewOrderSender.rejected, (state, action) => {
      state.errorSenders = action.payload.error || null;
      state.isOrderSenderAdd = false;
      state.isOrderSenderAddFail = true;
    });

    builder.addCase(updateOrderSender.fulfilled, (state, action) => {
      state.ordersSenders = state.ordersSenders.map((orderSender) =>
        orderSender._id.toString() === action.payload._id.toString()
          ? { ...orderSender, ...action.payload }
          : orderSender
      );
      state.isOrderSenderUpdate = true;
      state.isOrderSenderUpdateFail = false;
    });

    builder.addCase(updateOrderSender.rejected, (state, action) => {
      state.errorSender = action.payload.error || null;
      state.isOrderSenderUpdate = false;
      state.isOrderSenderUpdateFail = true;
    });

    builder.addCase(deleteOrderSender.fulfilled, (state, action) => {
      state.ordersSenders = (state.ordersSenders || []).filter(
        (orderSender) =>
          orderSender._id.toString() !== action.payload.id.toString()
      );

      state.isOrderSenderDelete = true;
      state.isOrderSenderDeleteFail = false;
    });

    builder.addCase(deleteOrderSender.rejected, (state, action) => {
      state.errorSender = action.payload.error || null;
      state.isOrderSenderDelete = false;
      state.isOrderSenderDeleteFail = true;
    });

    //==================================[ /getCustomClearances/ ]=======================================
    builder.addCase(getCustomClearances.pending, (state) => {
      state.isLoadingCustomClearances = true;
      state.isCustomClearancesSuccess = false;
      state.errorCustomClearances = null;
    });

    builder.addCase(getCustomClearances.fulfilled, (state, action) => {
      state.isLoadingCustomClearances = false;
      state.isCustomClearancesSuccess = true;
      state.customClearances = action.payload;
    });

    builder.addCase(getCustomClearances.rejected, (state, action) => {
      state.isLoadingCustomClearances = false;
      state.isCustomClearancesSuccess = false;
      state.errorCustomClearances = action.error.message;
    });

    builder.addCase(addNewCustomClearance.fulfilled, (state, action) => {
      state.customClearances.push(action.payload);
      state.isCustomClearanceAdd = true;
      state.isCustomClearanceAddFail = false;
    });

    builder.addCase(addNewCustomClearance.rejected, (state, action) => {
      state.errorCustomClearances = action.payload.error || null;
      state.isCustomClearanceAdd = false;
      state.isCustomClearanceAddFail = true;
    });

    builder.addCase(updateCustomClearance.fulfilled, (state, action) => {
      state.customClearances = state.customClearances.map((customClearance) =>
        customClearance._id.toString() === action.payload._id.toString()
          ? { ...customClearance, ...action.payload }
          : customClearance
      );
      state.isCustomClearanceUpdate = true;
      state.isCustomClearanceUpdateFail = false;
    });

    builder.addCase(updateCustomClearance.rejected, (state, action) => {
      state.errorCustomClearances = action.payload.error || null;
      state.isCustomClearanceUpdate = false;
      state.isCustomClearanceUpdateFail = true;
    });

    builder.addCase(deleteCustomClearance.fulfilled, (state, action) => {
      state.customClearances = (state.customClearances || []).filter(
        (customClearance) =>
          customClearance._id.toString() !== action.payload.id.toString()
      );

      state.isCustomClearanceDelete = true;
      state.isCustomClearanceDeleteFail = false;
    });

    builder.addCase(deleteCustomClearance.rejected, (state, action) => {
      state.errorCustomClearances = action.payload.error || null;
      state.isCustomClearanceDelete = false;
      state.isCustomClearanceDeleteFail = true;
    });

    //==================================[ /getCustomDeclarations/ ]=======================================
    builder.addCase(getCustomDeclarations.pending, (state) => {
      state.isLoadingCustomDeclarations = true;
      state.isCustomDeclarationsSuccess = false;
      state.errorCustomDeclarations = null;
    });

    builder.addCase(getCustomDeclarations.fulfilled, (state, action) => {
      state.isLoadingCustomDeclarations = false;
      state.isCustomDeclarationsSuccess = true;
      state.customDeclarations = action.payload;
    });

    builder.addCase(getCustomDeclarations.rejected, (state, action) => {
      state.isLoadingCustomDeclarations = false;
      state.isCustomDeclarationsSuccess = false;
      state.errorCustomDeclarations = action.error.message;
    });

    builder.addCase(addNewCustomDeclaration.fulfilled, (state, action) => {
      state.customDeclarations.push(action.payload);
      state.isCustomDeclarationAdd = true;
      state.isCustomDeclarationAddFail = false;
    });

    builder.addCase(addNewCustomDeclaration.rejected, (state, action) => {
      state.errorCustomDeclarations = action.payload.error || null;
      state.isCustomDeclarationAdd = false;
      state.isCustomDeclarationAddFail = true;
    });

    builder.addCase(updateCustomDeclaration.fulfilled, (state, action) => {
      state.customDeclarations = state.customDeclarations.map(
        (customDeclaration) =>
          customDeclaration._id.toString() === action.payload._id.toString()
            ? { ...customDeclaration, ...action.payload }
            : customDeclaration
      );
      state.isCustomDeclarationUpdate = true;
      state.isCustomDeclarationUpdateFail = false;
    });

    builder.addCase(updateCustomDeclaration.rejected, (state, action) => {
      state.errorCustomDeclarations = action.payload.error || null;
      state.isCustomDeclarationUpdate = false;
      state.isCustomDeclarationUpdateFail = true;
    });

    builder.addCase(deleteCustomDeclaration.fulfilled, (state, action) => {
      state.customDeclarations = (state.customDeclarations || []).filter(
        (customDeclaration) =>
          customDeclaration._id.toString() !== action.payload.id.toString()
      );

      state.isCustomDeclarationDelete = true;
      state.isCustomDeclarationDeleteFail = false;
    });

    builder.addCase(deleteCustomDeclaration.rejected, (state, action) => {
      state.errorCustomDeclarations = action.payload.error || null;
      state.isCustomDeclarationDelete = false;
      state.isCustomDeclarationDeleteFail = true;
    });
  },
});

export default ordersSlice.reducer;
